import { Card, CardContent, CardHeader, LinearProgress, Link, Stack, Typography } from '@mui/material';
import { t } from 'i18next';
import { ObjectDataViewer } from '../../../common/components/ObjectDataViewer';
import { BaseController, BaseState } from '../../../common/controllers/BaseController';
import { bind } from '../../../common/utils/decorators';
import { IntegrationsRepository } from '../../../integrations/services/IntegrationsRepository';
import { Venafi } from '../../../integrations/venafi/models/Venafi';
import { VenafiRepository } from '../../../integrations/venafi/services/VenafiRepository';
import { VenafiUploadForm } from '../components/VenafiUploadForm';
import { ReportCertificateDetail } from '../models/ReportCertificateDetail';

type State = BaseState & {
  venafi: Venafi | null;

  readingDetails: boolean;
  details: object | null;

  canUpload: boolean;
  uploadedGuid: string | null;
};

type Props = {
  certificate: ReportCertificateDetail | null;
};

export class ReportCertificateVenafiController extends BaseController<Props, State> {
  private readonly venafiRepository = new VenafiRepository();
  private readonly integrationsRepository = new IntegrationsRepository();

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.state = { venafi: null, readingDetails: false, details: null, canUpload: false, uploadedGuid: null };
  }

  componentDidMount(): void {
    super.componentDidMount();

    if (this.props.certificate) {
      this.load(this.props.certificate);
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    if (prevProps.certificate === null && this.props.certificate !== null) {
      this.load(this.props.certificate);
    }
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.venafiRepository.cancelAllRequests();
    this.integrationsRepository.cancelAllRequests();
  }

  render() {
    const { venafi, readingDetails, details, canUpload, uploadedGuid } = this.state;
    if (!venafi || !this.props.certificate) {
      return <></>;
    }
    const certificate = this.props.certificate;
    const guid = certificate.getVenafiGuid() ?? uploadedGuid;
    const fingerprint = certificate.getFingerprint();
    const url = guid ? venafi.certificateViewUrl(guid) : null;

    const UploadButton =
      // A certificate can only be uploaded if it has no GUID (does not exist in the Venafi inventory)
      // and the user has the required permissions
      !guid && canUpload ? <VenafiUploadForm onSubmit={(policy) => this.upload(certificate, policy)} /> : null;

    return this.renderBase(
      <Card>
        <CardHeader
          sx={UploadButton ? { pt: 1 } : {}}
          title={
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h5" component="span" sx={UploadButton ? { mt: -1 } : {}}>
                {t('venafiDetails')}
              </Typography>
              {UploadButton}
            </Stack>
          }
        />
        <CardContent sx={UploadButton ? { paddingTop: 0 } : {}}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1}>
              <Typography sx={{ fontWeight: 600 }}>{t('fingerprint')}:</Typography>
              <Typography>{fingerprint}</Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Typography sx={{ fontWeight: 600 }}>{t('venafiGuid')}:</Typography>
              <Typography>{guid ?? t('notFound')}</Typography>
            </Stack>
            {url && (
              <Stack direction="row" spacing={1}>
                <Typography sx={{ fontWeight: 600 }}>{t('venafiLink')}:</Typography>
                <Link
                  sx={{ lineHeight: '1.5rem' }}
                  href={url}
                  target="_blank"
                  aria-label={t('a11y.report.certificates.venafiGuidLabel', { certId: certificate.getId() }) as string}
                >
                  {url}
                </Link>
              </Stack>
            )}
            {readingDetails && <LinearProgress variant="indeterminate" />}
            {!readingDetails && details && <ObjectDataViewer value={details} loading={false} />}
          </Stack>
        </CardContent>
      </Card>
    );
  }

  private async load(certificate: ReportCertificateDetail) {
    if (!certificate.getFingerprint() || !certificate.isReportVenafiPopulated()) {
      return;
    }
    try {
      const [integrations, canReadDetails, canUpload] = await Promise.all([
        this.integrationsRepository.getIntegrations(),
        this.permissionsService.canReadCertificateVenafiDetails(certificate.getId()),
        this.permissionsService.canUploadCertificateToVenafi(certificate.getId()),
      ]);
      const venafi = integrations.getVenafi();
      this.setState({ venafi, canUpload });
      if (venafi && certificate.getVenafiGuid() && canReadDetails) {
        this.fetchDetails(certificate);
      }
    } catch (e: unknown) {
      this.handleError(e, undefined, true);
    }
  }

  @bind
  private async fetchDetails(certificate: ReportCertificateDetail) {
    try {
      this.setState({ readingDetails: true });
      const details = await this.venafiRepository.getCertificateDetails(certificate.getId());
      this.setState({ readingDetails: false, details });
    } catch (e: unknown) {
      this.setState({ readingDetails: false });
      this.handleError(e);
    }
  }

  @bind
  private async upload(certificate: ReportCertificateDetail, policy: string) {
    try {
      const uploadedGuid = await this.venafiRepository.uploadCertificate(certificate.getId(), policy);
      this.setState({ uploadedGuid, canUpload: false });
      this.fetchDetails(certificate);
    } catch (e: unknown) {
      this.handleError(e);
    }
  }
}
